/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { LuisText } from 'src/core/components/LuisText';
import { PillProps } from 'src/core/components/Pill';
import { PillContainer } from 'src/core/components/PillContainer';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { EntityRoleStore } from 'src/luis/types/stores/entities/EntityRoleStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';

export type EntityRolesProps = {
    /**
     * The displayed roles' entity.
     */
    entityStore: EntityStore;
};

const messages = defineMessages({
    directions: {
        id: 'newEntityDetails.entityRoles.directions',
        defaultMessage:
            'A role is a named alias for an entity based on context within the utterance. A role can be used  in both example utterances and patterns.'
    },
    noRolesMsg: {
        id: 'newEntityDetails.entityRoles.noRolesMsg',
        defaultMessage: 'Add a role'
    },
    createEntityRolePlaceholder: {
        id: 'newEntityDetails.entityRoles.createEntityRolePlaceholder',
        defaultMessage: 'Type role ...'
    },
    deleteRoleTitle: {
        id: 'newEntityDetails.entityRoles.deleteRoleTitle',
        defaultMessage: 'Delete role?'
    },
    deleteRoleDescription: {
        id: 'newEntityDetails.entityRoles.deleteRoleDescription',
        defaultMessage: 'Are you sure you want to delete this role?'
    },
    addRoleToastStart: {
        id: 'newEntityDetails.entityRoles.addEntityToastStart',
        defaultMessage: 'Adding role ...'
    },
    addRoleToastSuccess: {
        id: 'newEntityDetails.entityRoles.addEntityToastSuccess',
        defaultMessage: 'Add role completed'
    },
    addRoleToastFailed: {
        id: 'newEntityDetails.entityRoles.addEntityToastFailed',
        defaultMessage: 'Add role failed'
    },
    renameRoleToastStart: {
        id: 'newEntityDetails.entityRoles.renameRoleToastStart',
        defaultMessage: 'Renaming role ...'
    },
    renameRoleToastSuccess: {
        id: 'newEntityDetails.entityRoles.renameRoleToastSuccess',
        defaultMessage: 'Rename role completed'
    },
    renameRoleToastFailed: {
        id: 'newEntityDetails.entityRoles.renameRoleToastFailed',
        defaultMessage: 'Rename role failed'
    },
    deleteRoleToastStart: {
        id: 'newEntityDetails.entityRoles.deleteRoleToastStart',
        defaultMessage: 'Deleting role ...'
    },
    deleteRoleToastSuccess: {
        id: 'newEntityDetails.entityRoles.deleteRoleToastSuccess',
        defaultMessage: 'Delete role completed'
    },
    deleteRoleToastFailed: {
        id: 'newEntityDetails.entityRoles.deleteRoleToastFailed',
        defaultMessage: 'Delete role failed'
    }
});

const theme = getScopedTheme('EntityRoles');
const { styleComponent } = getStylistV2('EntityRoles');

const Container = styleComponent<IStackProps>(Stack)('Container', { paddingTop: '24px', paddingLeft: '13px' });

/**
 * A component to display and manage a specific entity's roles.
 */
export const EntityRoles = observer((props: EntityRolesProps) => {
    const { entityStore } = props;
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();

    const [inputPillValue, setInputPillValue] = React.useState('');
    const [showInputPill, setShowInputPill] = React.useState(false);
    const [isDeleteRoleModalHidden, setIsDeleteRoleModalHidden] = React.useState(true);
    const [roleToDelete, setRoleToDelete] = React.useState<EntityRoleStore>(null);

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);
    const entityRoles = entityStore.entityRoleStoreHouse.items;

    const rolePills = entityRoles.map<PillProps>((role, index) => ({
        pillKey: role.id,
        index,
        name: role.name,
        isPillEditable: canWrite,
        onPillValueChange: newName => onRenameRole(role, newName),
        onRemovePill: () => openDeleteModal(role),
        pillInputTextPlaceholder: localize(messages.createEntityRolePlaceholder)
    }));

    /**
     * Opens the role delete modal and assigns
     * the role to delete to the one given.
     *
     * @param role The role to delete.
     */
    const openDeleteModal = (role: EntityRoleStore) => {
        setIsDeleteRoleModalHidden(false);
        setRoleToDelete(role);
    };

    /**
     * Closes the modal and resets the role to delete
     * state variable.
     */
    const closeDeleteModal = () => {
        setIsDeleteRoleModalHidden(true);
        setRoleToDelete(null);
    };

    const addRoleTracker = useProgressTracker({
        onComplete: () => {
            setInputPillValue('');
            setShowInputPill(false);
        }
    });

    const deleteTracker = useProgressTracker({
        onComplete: () => {
            closeDeleteModal();
        }
    });

    /**
     * Adds a new role with the given role name.
     *
     * @param roleName The name of the role to add.
     */
    const onAddNewRole = (roleName: string) =>
        dispatcher.dispatch('addEntityRole', {
            entityId: entityStore.id,
            roleName,
            tracker: addRoleTracker,
            toastData: {
                running: { title: localize(messages.addRoleToastStart) },
                completed: { title: localize(messages.addRoleToastSuccess) },
                failed: { title: localize(messages.addRoleToastFailed) }
            }
        });

    /**
     * Deletes the currently roles selected roles.
     */
    const onDeleteRoles = () => {
        dispatcher.dispatch('deleteEntityRoles', {
            entityId: entityStore.id,
            roleIds: [roleToDelete.id],
            tracker: deleteTracker,
            toastData: {
                running: { title: localize(messages.deleteRoleToastStart) },
                completed: { title: localize(messages.deleteRoleToastSuccess) },
                failed: { title: localize(messages.deleteRoleToastFailed) }
            }
        });
    };

    /**
     * Renames the currently selected role.
     *
     * @param role The role to rename.
     * @param newName The new name to assign to the role.
     */
    const onRenameRole = (role: EntityRoleStore, newName: string) => {
        if (role.name === newName) {
            return;
        }

        dispatcher.dispatch('renameEntityRole', {
            roleId: role.id,
            newName,
            entityId: entityStore.id,
            toastData: {
                running: { title: localize(messages.renameRoleToastStart) },
                completed: { title: localize(messages.renameRoleToastSuccess) },
                failed: { title: localize(messages.renameRoleToastFailed) }
            }
        });
    };

    return (
        <Container tokens={{ childrenGap: 30 }}>
            <LuisText variant="medium" color={theme.primaryFontColor}>
                {localize(messages.directions)}
            </LuisText>

            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                <PillContainer
                    pills={rolePills}
                    onAddNewPill={onAddNewRole}
                    addPillButtonRole="listitem"
                    showInputPill={showInputPill}
                    isLuisToolTipHidden={canWrite}
                    inputPillValue={inputPillValue}
                    isAddPillButtonDisabled={!canWrite}
                    setInputPillValue={setInputPillValue}
                    isDeletePillButtonDisabled={!canDeleteData}
                    onRemovePill={() => setShowInputPill(false)}
                    onAddIconClick={() => setShowInputPill(true)}
                    inputPillPlaceholder={localize(messages.createEntityRolePlaceholder)}
                    allowAddPills={canWrite && entityRoles.length < LuisConstants.ResourceLimits.EntityRoleLimit}
                    disabledButtonLuisToolTipMessage={localize(globalMessages.cannotPerformAction, { displayedUserRole })}
                />

                {!entityRoles.length && (
                    <LuisText variant="small" color={theme.secondaryFontColor}>
                        {localize(messages.noRolesMsg)}
                    </LuisText>
                )}
            </Stack>

            <DeletePromptModal
                disabled={deleteTracker.isRunning}
                hidden={isDeleteRoleModalHidden}
                onDelete={onDeleteRoles}
                dialogContentProps={{
                    title: localize(messages.deleteRoleTitle),
                    subText: localize(messages.deleteRoleDescription)
                }}
                onDismiss={closeDeleteModal}
            />
        </Container>
    );
});
